/* eslint-disable react/no-did-update-set-state */
import ApiService from 'Services/ApiService';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { getUsecaseCategoryOptions, getUsecaseCategoryById } from 'Utils/GenericUtils';
import Enums from 'Constants/Enums';
import ReCAPTCHA from 'react-google-recaptcha';
import Select from 'react-select';
import { extractError } from 'Utils/ErrorUtils';
import { PUBLIC_EMAIL_PROVIDERS } from 'Constants/PublicEmails';
import { COUNTRIES } from 'Constants/Common';
import TranslationUtils from 'Utils/TranslationUtils';
import { FloatingInput } from '@piplapps/pipl-ui';
import './ExistingCustomerVettingStyle.less';
import styles from './ExistingCustomerVettingFormStyles.module.less';
import { trackEvent } from '../utils';

function extractDomain(email) {
  return email.replace(/.*@/, '');
}

function isWorkEmail(email) {
  const WHITE_LIST = PUBLIC_EMAIL_PROVIDERS;
  const domain = extractDomain(email);

  return !WHITE_LIST.includes(domain);
}

function isEmail(email) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
}

class ExistingCustomerVettingForm extends React.Component {
  state = {
    showEmailWarning: false,
    emailExistsError: false,
    countryInputValue: '',
    countryFocus: false,
    showCaptcha: false,
  };

  _showedEmailMsg = false;

  async componentDidUpdate(prevProps, prevState) {
    const oldEmail = prevProps.values.email;
    const currentEmail = this.props.values.email;

    if (this.props.error && this.props.error !== prevProps.error) {
      if (this._captchaRef) {
        this._captchaRef.reset();
      }
    }

    if (isEmail(currentEmail)) {
      if (currentEmail !== oldEmail) {
        if (isWorkEmail(currentEmail)) {
          this.setState({
            showEmailWarning: false,
          });
        } else {
          this.setState({
            showEmailWarning: true,
          });
        }
      }
    }
  }

  _getDropdownStyle = (inputValue, error) => {
    return {
      // option: (provided, state) => ({
      //   ...provided,
      //   // borderBottom: '1px dotted pink',
      //   // color: state.isSelected ? 'red' : 'blue',
      //   // padding: 20,
      // }),
      control: (provided) => {
        const controlStyle = {
          // none of react-select's styles are passed to <Control />
          // width: 200,
          ...provided,
          height: 40,
          borderColor: '#7c879f',
          fontSize: '16px',
        };

        if (inputValue) {
          controlStyle.borderColor = '#44D7B6';
          controlStyle.boxShadow = '0';
          controlStyle['&:hover'] = {
            borderColor: '#44D7B6',
            boxShadow: '0 0 0 1px #44D7B6',
          };
        }

        if (error) {
          controlStyle.borderColor = 'red';
          controlStyle.boxShadow = '0';
          controlStyle['&:hover'] = {
            borderColor: 'red',
            boxShadow: '0 0 0 1px red',
          };
        }

        return controlStyle;
      },
      singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';

        return { ...provided, opacity, transition, paddingLeft: 15 };
      },
    };
  };

  _getError = (fieldName) => {
    const error =
      (this.props.errors[fieldName] && this.props.touched[fieldName]) || this.forcedError
        ? this.props.errors[fieldName] || this.forcedError
        : '';

    return error;
  };

  _onFieldBlur = (e, fieldName) => {
    trackEvent(fieldName, e.target.value);
  };

  renderFirstNameField = () => {
    return (
      <div className={styles.fieldRow}>
        <Field
          onFieldBlur={this._onFieldBlur}
          icon="name"
          component={FloatingInput}
          name="firstName"
          type="text"
          placeholder={TranslationUtils.getText('FIRST_NAME')}
        />
      </div>
    );
  };

  renderOtherUseCaseField = () => {
    if (!this.props.values?.usecaseCategory) {
      return null;
    }

    const current = getUsecaseCategoryById(this.props.values.usecaseCategory);

    if (current.code !== 'OTHER') {
      return null;
    }

    return (
      <div className={styles.fieldRow}>
        <Field
          onFieldBlur={this._onFieldBlur}
          icon="name"
          component={FloatingInput}
          name="customUsecase"
          type="text"
          placeholder={TranslationUtils.getText('CUSTOM_USE_CASE')}
        />
      </div>
    );
  };

  renderLastNameField = () => {
    return (
      <div className={styles.fieldRow}>
        <Field
          onFieldBlur={this._onFieldBlur}
          icon="name"
          component={FloatingInput}
          name="lastName"
          type="text"
          placeholder={TranslationUtils.getText('LAST_NAME')}
        />
      </div>
    );
  };

  renderEmailField = () => {
    return (
      <div className={styles.fieldRow}>
        <Field
          onFieldBlur={async (event) => {
            try {
              this._onFieldBlur(event, 'email');
              const email = event.target.value;
              if (email && isEmail(email)) {
                await ApiService.vettingCheckEmail({ email });
                this.setState({
                  emailExistsError: false,
                });
              }
            } catch (e) {
              const error = e?.response?.data?.[0];
              if (error) {
                trackEvent(error, event.target.value);
              }
              this.setState({
                emailExistsError: TranslationUtils.getText(extractError(error)),
              });

              this.props.setFieldError('email', TranslationUtils.getText(extractError(error)));
            }
          }}
          forcedError={this.state.emailExistsError ? this.state.emailExistsError : ''}
          icon="email"
          component={FloatingInput}
          name="email"
          type="text"
          placeholder={TranslationUtils.getText('EMAIL')}
        />
        {!this.state.emailExistsError && this.state.showEmailWarning && (
          <>
            <div className={styles.emailWarn}>{TranslationUtils.getText('EMAIL_NON_WORK_WARNING')} </div>
          </>
        )}
      </div>
    );
  };

  renderCompanyNameField = () => {
    return (
      <div className={styles.fieldRow}>
        <Field
          onFieldBlur={this._onFieldBlur}
          icon="career"
          component={FloatingInput}
          name="company"
          type="text"
          placeholder={TranslationUtils.getText('COMPANY_NAME')}
        />
      </div>
    );
  };

  renderCountryField = () => {
    const options = this.getCountryOptions();
    const fieldName = 'country';
    const error = this._getError(fieldName);
    const inputValue = this.props.values[fieldName];

    const customStyles = {
      container: (provided) => ({
        ...provided,
        zIndex: 4000,
      }),
      control: (provided) => {
        const controlStyle = {
          // none of react-select's styles are passed to <Control />
          // width: 200,
          ...provided,
          zIndex: 4001,
          height: 40,
          borderColor: '#7c879f',
          fontSize: '16px',
        };

        if (inputValue) {
          controlStyle.borderColor = '#44D7B6';
          controlStyle.boxShadow = '0';
          controlStyle['&:hover'] = {
            borderColor: '#44D7B6',
            boxShadow: '0 0 0 1px #44D7B6',
          };
        }

        if (error) {
          controlStyle.borderColor = 'red';
          controlStyle.boxShadow = '0';
          controlStyle['&:hover'] = {
            borderColor: 'red',
            boxShadow: '0 0 0 1px red',
          };
        }

        return controlStyle;
      },
      input: (provided) => {
        return {
          ...provided,
          paddingLeft: 15,
        };
      },

      singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';

        return { ...provided, opacity, transition, paddingLeft: 15 };
      },
    };

    return (
      <div>
        <div className={styles.fieldRow} test-id="country-input">
          <FloatingInput
            labelStyle={{ zIndex: 4003, color: error ? 'red' : '#8792a9', paddingTop: '0px' }}
            value={inputValue}
            placeholder={TranslationUtils.getText('COUNTRY')}
            renderInput={({ onFocus, onBlur }) => {
              const innerBlur = () => {
                this.setState({ countryFocus: false });

                onBlur();
              };

              return (
                <Select
                  controlShouldRenderValue={!this.state.countryFocus}
                  value={options.find((o) => o.value === this.props.values[fieldName])}
                  onInputChange={(val, action) => {
                    this.setState({ countryInputValue: val });
                  }}
                  inputValue={this.state.countryInputValue}
                  onBlur={innerBlur}
                  onFocus={() => {
                    const current = options.find((o) => o.value === this.props.values[fieldName]);

                    if (current) {
                      this.setState({ countryInputValue: current.label });
                    }
                    this.setState({ countryFocus: true });
                    onFocus();
                  }}
                  blurInputOnSelect
                  type="select"
                  name="country"
                  clearable={false}
                  searchable={false}
                  editable={false}
                  placeholder=""
                  onChange={({ value }) => {
                    trackEvent(fieldName, value);
                    this.props.setFieldValue(fieldName, value);
                  }}
                  styles={customStyles}
                  menuContainerStyle={{ zIndex: 2333 }}
                  options={options}
                  inputProps={{
                    id: 'country-select',
                    'test-id': 'country-select',
                  }}
                />
              );
            }}
          />
          {error && (
            <div test-id={`error-${fieldName}`} className="error-label">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderPhoneNumberField = () => {
    return (
      <div className={styles.fieldRow}>
        <Field
          onFieldBlur={this._onFieldBlur}
          icon="phone"
          component={FloatingInput}
          name="phone"
          type="text"
          placeholder={TranslationUtils.getText('PHONE_NUMBER')}
        />
      </div>
    );
  };

  renderSeatsNumberField = () => {
    if (this.props.product === Enums.PRODUCT_TYPE.API_PURCHASE || this.props.product === Enums.PRODUCT_TYPE.API_TRIAL) {
      return null;
    }

    return (
      <div className={styles.fieldRow}>
        <Field
          onFieldBlur={this._onFieldBlur}
          icon="seats"
          component={FloatingInput}
          name="seatsNumber"
          type="number"
          placeholder={TranslationUtils.getText('SEATS_NUMBER')}
        />
      </div>
    );
  };

  renderUsecaseCategoryField = () => {
    const options = getUsecaseCategoryOptions();
    const fieldName = 'usecaseCategory';
    const error = this._getError(fieldName);
    const inputValue = this.props.values[fieldName];
    const customStyles = {
      container: (provided) => ({
        ...provided,
        zIndex: 3500,
      }),
      control: (provided) => {
        const controlStyle = {
          // none of react-select's styles are passed to <Control />
          // width: 200,
          ...provided,
          zIndex: 3501,
          height: 40,
          borderColor: '#7c879f',
          fontSize: '16px',
        };
        if (inputValue) {
          controlStyle.borderColor = '#44D7B6';
          controlStyle.boxShadow = '0';
          controlStyle['&:hover'] = {
            borderColor: '#44D7B6',
            boxShadow: '0 0 0 1px #44D7B6',
          };
        }
        if (error) {
          controlStyle.borderColor = 'red';
          controlStyle.boxShadow = '0';
          controlStyle['&:hover'] = {
            borderColor: 'red',
            boxShadow: '0 0 0 1px red',
          };
        }
        return controlStyle;
      },
      singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';

        return { ...provided, opacity, transition, paddingLeft: 15 };
      },
    };

    return (
      <div>
        <div className={styles.fieldRow} test-id="usecase-category-input">
          <FloatingInput
            labelStyle={{ zIndex: 3502, color: error ? 'red' : '#8792a9', paddingTop: '0px' }}
            value={inputValue}
            placeholder={TranslationUtils.getText('USECASE_CATEGORY')}
            renderInput={() => {
              return (
                <Select
                  type="select"
                  name={fieldName}
                  clearable={false}
                  isSearchable={false}
                  placeholder=""
                  onChange={({ value }) => {
                    trackEvent(fieldName, value);
                    this.props.setFieldValue(fieldName, value);
                    this.props.setFieldValue('usecaseSubCategory', '');
                    this.setState({ showCaptcha: true });
                  }}
                  styles={customStyles}
                  options={options}
                  inputProps={{
                    id: 'usecase-category',
                    'test-id': 'usecase-category',
                  }}
                />
              );
            }}
          />
          {error && (
            <div test-id={`error-${fieldName}`} className="error-label">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderUsecaseSubCategoryField = () => {
    const parentCategory = this.props.values.usecaseCategory;
    if (!parentCategory) {
      return null;
    }

    const options = getUsecaseCategoryOptions(parentCategory);
    if (Object.keys(options).length === 0) {
      return null;
    }

    const fieldName = 'usecaseSubCategory';
    const error = this._getError(fieldName);
    const inputValue = this.props.values[fieldName];
    const customStyles = this._getDropdownStyle(inputValue, error);

    return (
      <div>
        <div className={styles.fieldRow} test-id="usecase-sub-category-input">
          <FloatingInput
            key={parentCategory}
            labelStyle={{ zIndex: 2500, color: error ? 'red' : '#8792a9', paddingTop: '0px' }}
            value={this.props.values[fieldName]}
            placeholder={TranslationUtils.getText('USECASE_SUB_CATEGORY')}
            renderInput={() => {
              return (
                <Select
                  type="select"
                  name={fieldName}
                  clearable={false}
                  isSearchable={false}
                  value={options.find((op) => op.value === this.props.values[fieldName])}
                  placeholder=""
                  onChange={({ value }) => {
                    trackEvent(fieldName, value);
                    this.props.setFieldValue(fieldName, value);
                  }}
                  styles={customStyles}
                  options={options}
                  menuContainerStyle={{ zIndex: 999 }}
                  inputProps={{
                    id: 'usecase-sub-category',
                    'test-id': 'usecase-sub-category',
                  }}
                />
              );
            }}
          />
          {error && (
            <div test-id={`error-${fieldName}`} className="error-label">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderAgreeToTerms = () => {
    const fieldName = 'agreeToTerms';
    const error = this._getError(fieldName);

    return (
      <div className={styles.fieldRow}>
        <div className={styles.checkboxSection}>
          <div>
            <label htmlFor="terms-agree" className={styles.checkboxContainer}>
              <span className={styles.checkboxLabel}>
                {TranslationUtils.getText('AGREE_TO_TERMS')}{' '}
                <a target="_blank" href={this._getTermsURL()}>
                  {TranslationUtils.getText('CONDITION_OF_USE')}
                </a>{' '}
                {TranslationUtils.getText('AND')}{' '}
                <a target="_blank" href="https://pipl.com/resources/privacy-documents/privacy-policy-individuals">
                  {TranslationUtils.getText('PRIVACY_NOTICE')}
                </a>
              </span>
              <input
                onBlur={(e) => {
                  this._onFieldBlur(e, fieldName);
                }}
                type="checkbox"
                name={fieldName}
                onChange={() => {
                  const isOn = this.props.values[fieldName];

                  this.props.setFieldValue(fieldName, !isOn);
                }}
                checked={this.props.values[fieldName]}
                test-id="input-terms"
                id="terms-agree"
              />
              <span className={styles.checkmark} />
            </label>
          </div>
        </div>
        {error && (
          <div test-id={`error-${fieldName}`} className="error-label terms">
            {error}
          </div>
        )}
      </div>
    );
  };

  renderCaptcha = () => {
    if (!this.state.showCaptcha) {
      return null;
    }

    const isAutomation = document.cookie.indexOf('automation_tests') > -1;

    if (isAutomation) {
      return null;
    }

    const fieldName = 'captcha';
    const error = this._getError(fieldName);

    return (
      <div className={[styles.fieldRow, styles.captcha].join(' ')}>
        <div className={styles.recaptchaSection}>
          <ReCAPTCHA
            ref={(ref) => {
              this._captchaRef = ref;
            }}
            onChange={(value) => {
              trackEvent(fieldName, fieldName);
              this.props.setFieldValue(fieldName, value);
            }}
            className={`recaptcha-wrap ${styles.g_recaptcha}`}
            sitekey={window.__CONFIG__.RECAPTCHA_KEY}
          />
        </div>
        {error && (
          <div test-id={`error-${fieldName}`} className="error-label">
            {TranslationUtils.getText(error)}
          </div>
        )}
      </div>
    );
  };

  renderFields = () => {
    const fields = {
      LAST_NAME: {
        render: this.renderLastNameField,
      },

      FIRST_NAME: {
        render: this.renderFirstNameField,
      },

      COMPANY_NAME: {
        render: this.renderCompanyNameField,
      },

      PHONE_NUMBER: {
        render: this.renderPhoneNumberField,
      },
      COUNTRY: {
        render: this.renderCountryField,
      },
      /*
      SEATS_NUMBER: {
        render: this.renderSeatsNumberField,
      },
      */

      USECASE_CATEGORY: {
        render: this.renderUsecaseCategoryField,
      },

      USECASE_SUB_CATEGORY: {
        render: this.renderUsecaseSubCategoryField,
      },
      OTHER_USE_CASE_CATEGORY: {
        render: this.renderOtherUseCaseField,
      },
      AGREE_TO_TERMS: {
        render: this.renderAgreeToTerms,
      },
    };

    return Object.keys(fields)
      .sort((first, second) => {
        return this.getFieldOrder(first) - this.getFieldOrder(second);
      })
      .map((field, i) => {
        const FieldComponent = fields[field].render;

        // eslint-disable-next-line react/no-array-index-key
        return <FieldComponent key={i.toString()} {...this.props} />;
      });
  };

  getCountryOptions = () => {
    return COUNTRIES.map((field, i) => {
      return {
        label: field[1],
        value: field[0],
      };
    });
  };

  getFieldOrder = (fieldName) => {
    const fieldOrder = window.__CONFIG__.FIELDS_ORDER[fieldName.toUpperCase()];

    return fieldOrder ? fieldOrder.order : 100; // fields without order comes last
  };

  _getTermsURL = () => {
    if (this.props.product === Enums.PRODUCT_TYPE.API_PURCHASE || this.props.product === Enums.PRODUCT_TYPE.API_TRIAL) {
      return 'https://pipl.com/pipl-api-tos';
    }

    return 'https://pipl.com/pipl-search-tos';
  };

  render() {
    const {
      setFieldError,
      setFieldValue,
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      handleSubmit,
      isSubmitting,
      isLoading,
      isValid,
    } = this.props;

    return (
      <form className={styles.formWrapper} onSubmit={handleSubmit} id="existing-vetting-form">
        {this.renderFields()}

        <div>
          <button
            test-id="submit-button"
            className={`${styles.submitButton} ${(!isValid || isLoading) && styles.buttonDisabled}`}
            type="submit"
            disabled={isLoading}
          >
            {TranslationUtils.getText('EXISTING_CUSTOMER_VETTING_FORM_SUBMIT')}
            {isLoading && (
              <span className={styles.spinnerWrapper}>
                <span className="glyphicon-wrapper">
                  <span className="glyphicon glyphicon-refresh glyphicon-refresh-animate" />
                </span>
              </span>
            )}
          </button>
          {this.props.error && (
            <div style={{ marginTop: 10, color: 'red' }}>
              <span>{this.props.error}</span>
            </div>
          )}
        </div>
      </form>
    );
  }
}

ExistingCustomerVettingForm.propTypes = {
  setFieldValue: PropTypes.func,
  setFieldError: PropTypes.func,
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  isValid: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  product: PropTypes.string,
};

export default ExistingCustomerVettingForm;
